<template>
  <div>
    <div class="custom-card-header"></div>
    <DxDataGrid
      :data-source="store"
      :row-auto-height="true"
      :show-borders="false"
      :show-column-lines="false"
      :allow-column-reordering="true"
      :allow-column-resizing="true"
      :column-auto-width="true"
      :show-column-headers="true"
      :show-colon="false"
      :remote-operations="true"
      :hover-state-enabled="false"
      :show-row-lines="false"
      :row-alternation-enabled="false"
      :no-data-text="$t('noDataTextSummary')"
      class="custom-grid custom-grid-food-samples"
      @exporting="onExporting"
      @cell-prepared="onCellPrepared"
      @context-menu-preparing="addMenuItems"
      @content-ready="init"
      @toolbar-preparing="onToolbarPreparing($event)"
      @data-error-occurred="onGridError"
    >
      <DxScrolling mode="standard" />
      <DxStateStoring
        :enabled="true"
        type="custom"
        storage-key="foodSamplesTable"
        :custom-load="loadState"
        :custom-save="customSave"
      />
      <DxPager
        :show-page-size-selector="true"
        :allowed-page-sizes="[5, 10, 20]"
        :show-info="true"
        info-text="{2} items"
        :visible="true"
      />
      <DxSearchPanel
        :visible="true"
        :width="300"
        :placeholder="$t('searchSampleCode')"
      />

      <DxExport :enabled="false" />

      <DxColumn
        :caption="$t('markers')"
        :allow-sorting="true"
        cell-template="cellTemplateCombined"
        :fixed="true"
        fixed-position="left"
        :width="160"
      />
      <DxColumn
        data-field="code"
        :caption="$t('agronomySample.summary.sampleCode')"
        alignment="center"
        :allow-header-filtering="false"
        :fixed="true"
        fixed-position="left"
        :width="160"
      />
      <DxColumn
        data-field="sample_type"
        :caption="$t('agronomySample.summary.sampleType')"
        alignment="center"
        :allow-header-filtering="false"
      />
      <DxColumn
        data-field="reception_date"
        :caption="$t('foodSamples.receptionDate')"
        alignment="center"
        data-type="date"
        :format="$helpers.getDateFormat(company)"
        :sort-order="'desc'"
        :allow-header-filtering="false"
        :word-wrap-enabled="true"
        :width="202"
      />
      <DxColumn
        data-field="end_date"
        :caption="$t('foodSamples.endDate')"
        alignment="center"
        data-type="date"
        :format="$helpers.getDateFormat(company)"
        :allow-header-filtering="false"
        :width="150"
      />
      <DxColumn
        data-field="n_superior_cmas"
        :caption="$t('foodSamples.n_superior_cmas')"
        alignment="center"
        :allow-header-filtering="false"
        :width="95"
      />
      <DxColumn
        data-field="n_superior_lmrs"
        :caption="$t('foodSamples.n_superior_lmrs')"
        alignment="center"
        :allow-header-filtering="false"
        :width="95"
      />
      <DxColumn
        data-field="n_positives"
        :caption="$t('foodSamples.noOfPositives')"
        alignment="center"
        :allow-header-filtering="false"
      />
      <DxColumn
        data-field="n_traces"
        :caption="$t('foodSamples.noOfTraces')"
        alignment="center"
        :allow-header-filtering="false"
      />
      <DxColumn
        data-field="sample_name"
        :caption="$t('agronomySample.summary.description')"
        alignment="left"
        :allow-header-filtering="false"
      />

      <DxColumn
        data-field="analyse_type"
        :caption="$t('foodSamples.analysisType')"
        alignment="center"
        :allow-header-filtering="false"
      />
      <DxColumn
        data-field="cultive_organic"
        :caption="$t('foodSamples.ecologic')"
        alignment="center"
        :allow-header-filtering="false"
        cell-template="cellTemplateOrganicOrNot"
      />
      <DxColumn
        data-field="study"
        :caption="$t('environmentSamples.summary.project')"
        alignment="center"
        :allow-header-filtering="false"
      />
      <DxColumn
        data-field="farm"
        :caption="$t('foodSamples.samplingSite')"
        alignment="center"
        :allow-header-filtering="false"
      />
      <DxColumn
        data-field="parcel"
        :caption="$t('environmentSamples.samplingPoint')"
        alignment="center"
        :allow-header-filtering="false"
      />
      <DxColumn
        data-field="third_client"
        :caption="$t('foodSamples.thirdClient')"
        alignment="center"
        :allow-header-filtering="false"
      />

      <DxColumn
        data-field="status"
        :caption="$t('agronomySample.summary.status')"
        alignment="left"
        :fixed="true"
        fixed-position="right"
        :allow-header-filtering="false"
        cell-template="cellTemplateStatus"
      />

      <DxColumn
        :buttons="updatedEditButtons"
        :fixed="true"
        fixed-position="right"
        type="buttons"
        :width="70"
      />

      <DxColumn
        v-for="chain in selectedEuropeanChains"
        :key="chain"
        :name="chain"
        :caption="chain"
        cell-template="cellTemplateChain"
      />
      <template #cellTemplateChain="{ data }">
        <div>
          <div
            v-if="
              data.data.retailers && data.data.retailers.length > 0
            "
          >
            <div
              v-if="
                data.data.retailers
                  .flatMap((el) => el.Supermercado)
                  .includes(data.column.caption)
              "
            >
              <div style="text-align: center">
                <img
                  width="20"
                  height="20"
                  :src="
                    getDotColorImage(
                      data.data.retailers.find(
                        (el) =>
                          el.Supermercado === data.column.caption
                      ).Confirmado
                        ? 'green'
                        : data.data.retailers.find(
                            (el) =>
                              el.Supermercado === data.column.caption
                          ).Confirmado === null ||
                          data.data.value_report === 'CMA'
                        ? 'grey'
                        : 'red'
                    )
                  "
                />
              </div>
            </div>
          </div>
          <div v-else style="text-align: center">
            <img
              width="20"
              height="20"
              :src="getDotColorImage('grey')"
            />
          </div>
        </div>
      </template>
      <template #cellTemplateOrganicOrNot="{ data }">
        <div v-if="data.value">{{ $t('yes') }}</div>
        <div v-else>{{ $t('no') }}</div>
      </template>
      <!-- <template #cellTemplateDots="{ data }">
        <div class="dots-parent-container">
          <img
            width="20"
            height="20"
            :src="getDotColorImage(data.data.summary_semaphore)"
          />
        </div>
      </template> -->
      <template #cellTemplateCombined="{ data }">
        <div class="combined-status-cell">
          <img
            v-if="data.data.summary_semaphore !== null"
            width="18"
            height="18"
            :src="getDotColorImage(data.data.summary_semaphore)"
            class="status-dot"
          />
          <span
            :class="
              $helpers.getStatusAlertClass(
                data.data.summary_semaphore
              )
            "
            class="status-text marker-text"
          >
            {{
              {
                green: $t('newInfoSemaphore.greenDotDescription'),
                orange: $t('newInfoSemaphore.orangeDotDescription'),
                grey: $t('newInfoSemaphore.greyDotDescription'),
                red: $t('newInfoSemaphore.redDotDescription'),
              }[data.data.summary_semaphore] || data.data.status
            }}
          </span>
        </div>
      </template>
      <template #cellTemplateStatus="{ data }">
        <div
          :class="$helpers.getStatusClass(data.data.status_code)"
          class="status-text"
          style="text-align: center"
        >
          {{ data.data.status }}
        </div>
      </template>
      <!-- <template #cellTemplateHeaderDots="{ data }">
        <div>
          <PopUp position="top">
            <div style="padding: 10px">
              <div class="pop-up-info">
                <img
                  :src="getDotColorImage('grey')"
                  width="15"
                  height="15"
                />
                <div style="padding-left: 5px">
                  {{ $t('foodSamples.summary.greyDotDescription') }}
                </div>
              </div>
              <div class="pop-up-info">
                <img
                  :src="getDotColorImage('green')"
                  width="15"
                  height="15"
                />
                <div style="padding-left: 5px">
                  {{ $t('foodSamples.summary.greenDotDescription') }}
                </div>
              </div>
              <div class="pop-up-info">
                <img
                  :src="getDotColorImage('orange')"
                  width="15"
                  height="15"
                />
                <div style="padding-left: 5px">
                  {{ $t('foodSamples.summary.orangeDotDescription') }}
                </div>
              </div>
              <div class="pop-up-info">
                <img
                  :src="getDotColorImage('red')"
                  width="15"
                  height="15"
                />
                <div style="padding-left: 5px">
                  {{ $t('foodSamples.summary.redDotDescription') }}
                </div>
              </div>
            </div>
          </PopUp>
        </div>
      </template> -->
    </DxDataGrid>

    <!-- <ContextMenu :id="'context-menu-btn'" :items="getItems" /> -->

    <NotificationSpinner
      v-if="notificationMessage.length > 0"
      :message="notificationMessage"
    />
  </div>
</template>
<script>
import { mapActions, mapState } from 'vuex';
import {
  DxDataGrid,
  DxColumn,
  DxSearchPanel,
  DxStateStoring,
  DxPager,
  DxPaging,
  DxScrolling,
  DxExport,
} from 'devextreme-vue/data-grid';

import saveAs from 'file-saver';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { Workbook } from 'exceljs';
import { trigger } from 'devextreme/events';
import {
  ALLOWED_SAMPLES_PARAMETERS,
  ATCS_ES,
  NOTIFY_TIME_LONG,
} from '@/js/constants';
import { downloadReportMixin } from '@/mixins/downloadReportMixin';
import NotificationSpinner from '@/components/NotificationSpinner/index.vue';
import iconVisibility from '@/static/icons/ic_visibility.svg';
import iconDownload from '@/static/icons/ic_download.svg';
import { getGridActionCellElement } from '@/js/gridActionCell';

export default {
  components: {
    DxDataGrid,
    DxColumn,
    DxSearchPanel,
    DxStateStoring,
    DxPager,
    DxPaging,
    DxScrolling,
    DxExport,
    NotificationSpinner,
  },
  mixins: [downloadReportMixin],
  props: {
    store: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      isFilterActive: {
        inProcess: false,
        finished: false,
        cancelled: false,
        lastMonth: false,
        onAlert: false,
        noPositives: false,
        noReviewed: false,
      },
      samplesData: {},
      editButtons: [
        {
          icon: 'find',
          onClick: this.viewAtc,
        },
        {
          icon: 'download',
          onClick: this.triggerContextualMenu,
        },
      ],
      itemsDisabled: [
        {
          icon: 'excel',
          text: this.$t('excelDownload'),
          onItemClick: () => {
            this.onExporting(this.grid);
          },
        },
        {
          icon: 'excel',
          text: this.$t('excelDownloadParameters'),
          onItemClick: () => {
            // this.onExporting(this.grid);
          },
          disabled: true,
        },
        {
          icon: 'pdf',
          text: this.$t('pdfReportDownload'),
          onItemClick: () => {
            // this.onExporting(this.grid);
          },
          disabled: true,
        },
      ],
      itemsEnabled: [
        {
          icon: 'excel',
          text: this.$t('excelDownload'),
          onItemClick: () => {
            this.onExporting(this.grid);
          },
        },
        {
          icon: 'excel',
          text: this.$t('excelDownloadParameters'),
          onItemClick: async () => {
            try {
              this.setDisableContext(true);
              let message = this.$t('downloading');
              if (
                this.grid.component.totalCount() >
                ALLOWED_SAMPLES_PARAMETERS
              ) {
                message = this.$t('sampleParametersMustFilter');
              }
              this.notificationMessage = message;

              const samples = await this.getSamplesWithParameters();
              this.formatExcel(samples);
            } catch (e) {
              this.notificationMessage = '';
              this.$notifyDX(
                {
                  message: this.$t('downloadError'),
                  width: 350,
                },
                'error',
                NOTIFY_TIME_LONG
              );
            } finally {
              this.setDisableContext(false);

              setTimeout(() => {
                this.notificationMessage = '';
              }, 2000);
            }
          },
        },
        {
          icon: 'pdf',
          text: this.$t('pdfReportDownload'),
          onItemClick: async () => {
            this.setDisableContext(true);

            try {
              this.notificationMessage = this.$t('downloading');

              const b64 = await this.getSamplesZip(
                this.getSamplesPageDownloadParams()
              );

              const link = document.createElement('a');
              link.href = `data:application/octet-stream;base64,${b64}`;
              link.download = `besafer-reports-PDF-${new Date().toLocaleString()}.zip`;
              link.click();

              URL.revokeObjectURL(link.href);

              this.notificationMessage = this.$t('downloadedFile');
            } catch (e) {
              this.notificationMessage = '';
              this.$notifyDX(
                {
                  message: this.$t(
                    this.$helpers.getFilteredErrorMessage(
                      JSON.parse(e.message).error
                    )
                  ),
                  width: 550,
                },
                'error',
                NOTIFY_TIME_LONG
              );
            } finally {
              setTimeout(() => {
                this.notificationMessage = '';
              }, 2000);
              this.setDisableContext(false);
            }
          },
        },
      ],
      grid: null,
      notificationMessage: '',
    };
  },
  computed: {
    getItems() {
      return this.disableContext
        ? this.itemsDisabled
        : this.itemsEnabled;
    },
    ...mapState('contact', ['company']),
    ...mapState('samples', ['disableContext', 'isDownloadingReport']),
    ...mapState('foodFilter', ['selectedEuropeanChains']),
    ...mapState('samplesRegisterModule', ['invoiceSamples']),
    updatedEditButtons() {
      return this.isDownloadingReport
        ? [
            {
              icon: iconVisibility,
              onClick: this.viewAtc,
              cssClass: 'grid-button-icon',
            },
            {
              icon: iconDownload,
              onClick: this.download,
              disabled: true,
              cssClass: 'grid-button-icon',
            },
          ]
        : [
            {
              icon: iconVisibility,
              onClick: this.viewAtc,
              cssClass: 'grid-button-icon',
            },
            {
              icon: iconDownload,
              onClick: this.triggerContextualMenu,
              disabled: false,
              cssClass: 'grid-button-icon',
            },
          ];
    },
  },

  beforeMount() {
    this.setIsDownloadingReport(false);
    this.setDisableContext(false);

    // 1) Restaurar el estado de los botones desde localStorage
    const raw = localStorage.getItem('foodSamplesTable');
    if (raw) {
      try {
        const saved = JSON.parse(raw);
        if (saved.isFilterActive) {
          Object.assign(this.isFilterActive, saved.isFilterActive);
        }
      } catch (e) {
        console.warn('foodSamplesTable corrupto en localStorage', e);
      }
    }

    // 2) Flags que vienen del dashboard (sobrescriben lo anterior)
    const isAlert = this.$f7route?.query?.dashboardAlert === 'true';
    const isInProcess =
      this.$f7route?.query?.dashboardInProcess === 'true';
    const isNotReviewed =
      this.$f7route?.query?.dashboardNotReviewed === 'true';

    const fromDashboard = isAlert || isInProcess || isNotReviewed;

    if (isAlert) this.isFilterActive.onAlert = true;
    if (isInProcess) this.isFilterActive.inProcess = true;
    if (isNotReviewed) this.isFilterActive.noReviewed = true;

    const hasAnyFilter =
      this.isFilterActive.inProcess ||
      this.isFilterActive.finished ||
      this.isFilterActive.cancelled ||
      this.isFilterActive.lastMonth ||
      this.isFilterActive.noReviewed ||
      this.isFilterActive.onAlert ||
      this.isFilterActive.noPositives;
    if (fromDashboard) {
      return;
    }

    if (hasAnyFilter) {
      this.applyFilters();
    } else {
      this.setCurrentFilter('food');
      this.setAllFilterParams({ atc_name: ATCS_ES.food });
    }
  },

  mounted() {
    if (this.isFilterActive.onAlert && this.grid?.component) {
      this.$nextTick(() => {
        const toolbar = this.grid.component
          .element()
          .querySelector('.dx-toolbar');
        const alertButton = toolbar?.querySelector(
          '[aria-label="En alerta"]'
        );
        if (alertButton) {
          alertButton.classList.add('show-icon');
          alertButton.classList.remove('hide-icon');
        }
      });
    }
  },
  methods: {
    onGridError() {
      this.$notifyDX(
        {
          message: this.$t('internalServerError'),
          width: 550,
        },
        'error',
        NOTIFY_TIME_LONG
      );
    },
    getLastMonthDates() {
      const endDate = new Date(); // Hoy
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30); // Hoy menos 30 días

      const formatDate = (date) => {
        const d = new Date(date);
        let month = '' + (d.getMonth() + 1);
        let day = '' + d.getDate();
        const year = d.getFullYear();

        if (month.length < 2) month = '0' + month;
        if (day.length < 2) day = '0' + day;

        return [year, month, day].join('-');
      };

      return {
        receptionDate_after: formatDate(startDate),
        receptionDate_before: formatDate(endDate),
      };
    },

    applyFilters() {
      const params = {
        receptionDate_after: '',
        receptionDate_before: '',
        endDate_after: '',
        endDate_before: '',
        sample_type: '',
        analyse_type: '',
        atc_name: ATCS_ES.food,
        not_seen: '',
        summary_semaphore: '', // 🔴🟠 NUEVO
      };

      if (this.isFilterActive.lastMonth) {
        const { receptionDate_after, receptionDate_before } =
          this.getLastMonthDates();
        params.endDate_after = receptionDate_after;
        params.endDate_before = receptionDate_before;
      }

      const statuses = [];
      if (this.isFilterActive.inProcess) statuses.push(2);
      if (this.isFilterActive.finished) statuses.push(3);
      if (this.isFilterActive.cancelled) statuses.push(4);

      if (statuses.length > 0) {
        params.status = statuses.join(',');
      }

      // 🟡🔴🟢 Semáforos (ACUMULATIVO)
      const semaphores = [];

      if (this.isFilterActive.onAlert) {
        semaphores.push('red', 'orange');
      }

      if (this.isFilterActive.noPositives) {
        semaphores.push('green');
      }

      if (semaphores.length > 0) {
        params.summary_semaphore = semaphores.join(',');
      }
      if (this.isFilterActive.noReviewed) {
        params.not_seen = true;
      }
      this.setCurrentFilter('food');
      this.setAllFilterParams(params);
      this.$store.dispatch(
        'foodSamples/callStoreWithFilters',
        params,
        { root: true }
      );

      this.saveGridState();
    },

    saveGridState() {
      if (!this.grid || !this.grid.component) return;
      const gridState = this.grid.component.state();
      this.customSave(gridState);
    },

    getDotColorText(supermercado, valueReport, code) {
      const dataLayer = this.samplesData.find(
        (layer) => layer.code === code
      );

      if (!dataLayer || !dataLayer.retailers) return 'grey';

      const retailer = dataLayer.retailers.find(
        (el) => el.Supermercado === supermercado
      );

      if (retailer) {
        if (retailer.Confirmado === true) {
          return 'green';
        }
        if (retailer.Confirmado === false) {
          return 'red';
        }
        if (retailer.Confirmado === null || valueReport === 'CMA') {
          return 'grey';
        }
      }

      return 'grey';
    },

    loadState() {
      let state = localStorage.getItem('foodSamplesTable');
      if (!state) return null;

      state = JSON.parse(state);

      if (state.isFilterActive) {
        delete state.isFilterActive;
      }

      state.searchText = '';
      return state;
    },

    customSave(state) {
      // Descomentar para guardar estados de burbuja en grid
      const extendedState = {
        ...state,
        isFilterActive: this.isFilterActive,
      };
      localStorage.setItem(
        'foodSamplesTable',
        JSON.stringify(extendedState)
      );
    },

    getDotColorImage(color) {
      return require(`../../../../assets/dot-colors/${color}_new.svg`)
        .default;
    },

    formatExcel(samples) {
      const config = [
        {
          sheet: 'Samples',
          columns: [
            {
              label: this.$t('agronomySample.summary.sampleCode'),
              value: 'code',
            },
            {
              label: this.$t('agronomySample.summary.sampleType'),
              value: 'sample_type',
            },
            {
              label: this.$t('agronomySample.summary.description'),
              value: 'sample_name',
            },
            {
              label: this.$t('agronomySample.summary.lot_code'),
              value: 'lot_code',
            },
            {
              label: this.$t('foodSamples.receptionDate'),
              value: 'reception_date',
            },
            {
              label: this.$t('foodSamples.endDate'),
              value: 'end_date',
            },
            {
              label: this.$t('foodSamples.analysisType'),
              value: 'analyse_type',
            },
            {
              label: this.$t('foodSamples.ecologic'),
              value: 'cultive_organic',
            },
            {
              label: this.$t('foodSamples.thirdClient'),
              value: 'third_client',
            },
            {
              label: this.$t('foodSamples.n_superior_cmas'),
              value: 'food_aggregated_info.n_superior_cmas',
            },
            {
              label: this.$t('foodSamples.n_superior_lmrs'),
              value: 'food_aggregated_info.n_superior_lmrs',
            },
            {
              label: this.$t('foodSamples.noOfPositives'),
              value: 'food_aggregated_info.n_positives',
            },
            {
              label: this.$t('foodSamples.noOfTraces'),
              value: 'food_aggregated_info.n_traces',
            },
            {
              label: this.$t('environmentSamples.summary.project'),
              value: 'study',
            },
            {
              label: this.$t('foodSamples.samplingSite'),
              value: 'farm',
            },
            {
              label: this.$t('environmentSamples.samplingPoint'),
              value: 'parcel',
            },
            {
              label: this.$t('agronomySample.summary.status'),
              value: 'status',
            },
          ],
          content: [
            ...samples.map((sample) => {
              sample.parameters.forEach((parameter) => {
                sample.sample[
                  `${parameter.parameter} ${parameter.unit}`
                ] = parameter.value;
              });
              return sample.sample;
            }),
          ],
        },
      ];

      samples.forEach((sample) => {
        sample.parameters.forEach((parameter) => {
          config[0].columns.push({
            label: `${parameter.parameter} ${parameter.unit}`,
            value: (data) =>
              data[`${parameter.parameter} ${parameter.unit}`],
          });
        });
      });

      const fileSettings = {
        fileName: `besafer-samples-parameters-${this.$helpers.getDateFormatedDayMonthYear(
          new Date()
        )}`,
        extraLength: 3,
        writeOptions: {},
      };

      this.$jsonToXLSX(config, fileSettings, () => {
        this.setDisableContext(false);

        this.notificationMessage = this.$t(
          'xslxDownloadedSuccessfully'
        );
      });
    },

    onToolbarPreparing(e) {
      if (!e.toolbarOptions) return;
      if (!Array.isArray(e.toolbarOptions.items)) {
        e.toolbarOptions.items = [];
      }
      const toolbarItems = e.toolbarOptions.items;
      e.toolbarOptions.elementAttr = {
        class: 'custom-toolbar',
      };
      toolbarItems.forEach((item) => {
        if (item.name === 'searchPanel') {
          item.location = 'before';
        }
      });

      toolbarItems.push({
        name: 'Filtros',
        location: 'before',
        widget: 'dxButton',
        locateInMenu: 'never',
        options: {
          text: this.$t('filter'),
          icon: '././static/icons/ic_filter.svg',
          width: 115,
          elementAttr: {
            class: 'btn-filtrar',
          },
          onClick: () => {
            this.$f7.panel.open('right');
          },
        },
      });

      // Boton para Filtrar En alerta
      toolbarItems.push({
        name: 'En alerta',
        location: 'before',
        widget: 'dxButton',
        locateInMenu: 'never',
        options: {
          text: this.$t('onAlert'),
          icon: '././static/icons/ic_check.svg',
          width: 205,
          elementAttr: {
            class: `filtrosBotones ${
              this.isFilterActive.onAlert ? 'show-icon' : 'hide-icon'
            }`,
          },
          // onClick: (e) => {
          //   e.element.classList.toggle('show-icon');
          // },
          onClick: (e) => {
            this.isFilterActive.onAlert =
              !this.isFilterActive.onAlert;

            e.element.classList.toggle(
              'show-icon',
              this.isFilterActive.onAlert
            );
            e.element.classList.toggle(
              'hide-icon',
              !this.isFilterActive.onAlert
            );

            this.applyFilters('');
          },
        },
      });

      // Boton para Filtrar Sin revisar
      toolbarItems.push({
        name: 'Sin revisar',
        location: 'before',
        widget: 'dxButton',
        locateInMenu: 'never',
        options: {
          text: this.$t('notReviewed'),
          icon: '././static/icons/ic_check.svg',
          width: 170,
          elementAttr: {
            class: `filtrosBotones ${
              this.isFilterActive.noReviewed
                ? 'show-icon'
                : 'hide-icon'
            }`,
          },
          onClick: (e) => {
            this.isFilterActive.noReviewed =
              !this.isFilterActive.noReviewed;

            e.element.classList.toggle(
              'show-icon',
              this.isFilterActive.noReviewed
            );
            e.element.classList.toggle(
              'hide-icon',
              !this.isFilterActive.noReviewed
            );

            this.applyFilters();
          },
        },
      });

      // Boton para Filtrar Sin positivos
      toolbarItems.push({
        name: 'Sin positivos',
        location: 'before',
        widget: 'dxButton',
        locateInMenu: 'never',
        options: {
          text: this.$t('noPositives'),
          icon: '././static/icons/ic_check.svg',
          width: 150,
          elementAttr: {
            class: `filtrosBotones ${
              this.isFilterActive.noPositives
                ? 'show-icon'
                : 'hide-icon'
            }`,
          },
          // onClick: (e) => {
          //   e.element.classList.toggle('show-icon');
          // },
          onClick: (e) => {
            this.isFilterActive.noPositives =
              !this.isFilterActive.noPositives;

            e.element.classList.toggle(
              'show-icon',
              this.isFilterActive.noPositives
            );
            e.element.classList.toggle(
              'hide-icon',
              !this.isFilterActive.noPositives
            );

            this.applyFilters('');
          },
        },
      });

      // En proceso (status 2) persistente
      toolbarItems.push({
        name: 'En proceso',
        location: 'before',
        widget: 'dxButton',
        locateInMenu: 'never',
        options: {
          text: this.$t('inProcess'),
          icon: '././static/icons/ic_check.svg',
          width: 160,
          elementAttr: {
            class:
              'filtrosBotones ' +
              (this.isFilterActive.inProcess
                ? 'show-icon'
                : 'hide-icon'),
          },
          onClick: (e) => {
            this.isFilterActive.inProcess =
              !this.isFilterActive.inProcess;
            e.element.classList.toggle(
              'show-icon',
              this.isFilterActive.inProcess
            );
            e.element.classList.toggle(
              'hide-icon',
              !this.isFilterActive.inProcess
            );
            this.applyFilters('');
          },
        },
      });

      // Terminada (status 3) persistente
      toolbarItems.push({
        name: 'Terminada',
        location: 'before',
        widget: 'dxButton',
        locateInMenu: 'never',
        options: {
          text: this.$t('finished'),
          icon: '././static/icons/ic_check.svg',
          width: 150,
          elementAttr: {
            class:
              'filtrosBotones ' +
              (this.isFilterActive.finished
                ? 'show-icon'
                : 'hide-icon'),
          },
          onClick: (e) => {
            this.isFilterActive.finished =
              !this.isFilterActive.finished;
            e.element.classList.toggle(
              'show-icon',
              this.isFilterActive.finished
            );
            e.element.classList.toggle(
              'hide-icon',
              !this.isFilterActive.finished
            );
            this.applyFilters('');
          },
        },
      });

      // Cancelada (status 4) persistente
      // toolbarItems.push({
      //   name: 'Cancelada',
      //   location: 'before',
      //   widget: 'dxButton',
      //   locateInMenu: 'never',
      //   options: {
      //     text: this.$t('SamplesList.statusList.cancelled'),
      //     icon: '././static/icons/ic_check.svg',
      //     width: 155,
      //     elementAttr: {
      //       class:
      //         'filtrosBotones ' +
      //         (this.isFilterActive.cancelled
      //           ? 'show-icon'
      //           : 'hide-icon'),
      //     },
      //     onClick: (e) => {
      //       this.isFilterActive.cancelled =
      //         !this.isFilterActive.cancelled;
      //       e.element.classList.toggle(
      //         'show-icon',
      //         this.isFilterActive.cancelled
      //       );
      //       e.element.classList.toggle(
      //         'hide-icon',
      //         !this.isFilterActive.cancelled
      //       );
      //       this.applyFilters('');
      //     },
      //   },
      // });

      // Último mes persistente
      toolbarItems.push({
        name: 'Último mes',
        location: 'before',
        widget: 'dxButton',
        locateInMenu: 'never',
        options: {
          text: this.$t('lastMonth'),
          icon: '././static/icons/ic_check.svg',
          width: 139,
          elementAttr: {
            class:
              'filtrosBotones ' +
              (this.isFilterActive.lastMonth
                ? 'show-icon'
                : 'hide-icon'),
          },
          onClick: (e) => {
            this.isFilterActive.lastMonth =
              !this.isFilterActive.lastMonth;
            e.element.classList.toggle(
              'show-icon',
              this.isFilterActive.lastMonth
            );
            e.element.classList.toggle(
              'hide-icon',
              !this.isFilterActive.lastMonth
            );
            this.applyFilters('');
          },
        },
      });

      // Registrar muestra
      toolbarItems.push({
        name: 'Registrar muestra',
        location: 'after',
        widget: 'dxButton',
        locateInMenu: 'never',
        cssClass: 'registrar-arriba',
        options: {
          text: this.$t('registerSample'),
          elementAttr: {
            class: 'botones-registrar',
          },
          onClick: () => {
            this.GoSampleRegister();
          },
        },
      });

      // Descargas
      toolbarItems.push({
        name: 'Descargas',
        location: 'after',
        widget: 'dxDropDownButton',
        locateInMenu: 'never',
        cssClass: 'btn-estado-custom btn-descargar descargar-abajo',
        options: {
          text: this.$t('download'),
          elementAttr: { class: 'btn-estado-custom' },
          items: [
            { key: 'excel', text: this.$t('excelDownload') },
            { key: 'pdf', text: this.$t('pdfReportDownload') },
            {
              key: 'excelParams',
              text: this.$t('excelDownloadParameters'),
            },
          ],
          displayExpr: 'text',
          keyExpr: 'key',
          dropDownOptions: { width: 360 },
          splitButton: false,
          disabled: this.disableContext || this.isDownloadingReport,
          onItemClick: ({ itemData }) => {
            this.downloadSelected(itemData.key);
          },
        },
      });

      // Por estado (decorativo)
      toolbarItems.push({
        name: 'Por estado',
        location: 'after',
        widget: 'dxSelectBox',
        locateInMenu: 'never',
        visible: false,
        options: {
          placeholder: this.$t('byStatus'),
          elementAttr: {
            class: 'btn-estado',
          },
          dataSource: '',
          displayExpr: '',
          valueExpr: '',
          onValueChanged: (e) => {
            // console.log('Estado seleccionado:', e.value);
          },
          dropDownButtonTemplate() {
            return `
          <div class="dropdown-button">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12 14.677C11.8795 14.677 11.7673 14.6577 11.6635 14.6192C11.5597 14.5807 11.4609 14.5147 11.3673 14.4212L6.873 9.927C6.73467 9.7885 6.66384 9.61441 6.6605 9.40475C6.65734 9.19525 6.72817 9.018 6.873 8.873C7.018 8.72816 7.19367 8.65575 7.4 8.65575C7.60634 8.65575 7.782 8.72816 7.927 8.873L12 12.9462L16.073 8.873C16.2115 8.73466 16.3856 8.66383 16.5953 8.6605C16.8048 8.65733 16.982 8.72816 17.127 8.873C17.2718 9.018 17.3443 9.19366 17.3443 9.4C17.3443 9.60633 17.2718 9.782 17.127 9.927L12.6328 14.4212C12.5391 14.5147 12.4403 14.5807 12.3365 14.6192C12.2327 14.6577 12.1205 14.677 12 14.677Z" fill="#00346A"/>
            </svg>
          </div>
      `;
          },
        },
      });
    },

    GoSampleRegister() {
      try {
        if (this.invoiceSamples.length > 0) {
          this.setInvoiceSamples([]);
        }
        this.RegisterInvoice();
        this.$f7router.navigate(
          {
            name: 'SamplesRegister',
          },
          {
            animate: true,
            transition: 'f7-fade',
          }
        );
      } catch (error) {
        this.$notifyDX(
          {
            message: this.$t(
              this.$helpers.getFilteredErrorMessage(error.message)
            ),
            width: 550,
          },
          'error',
          NOTIFY_TIME_LONG
        );
      }
    },

    init(e) {
      const dataSource = e?.component?.getDataSource?.();
      const items = dataSource?.items?.() || [];
      this.setSamples(items);
      this.samplesData = items;
      this.grid = e;
    },

    triggerContextualMenu(e) {
      this.setReportSampleId(e.row.data.sample_id);
      this.setReportSampleCode(e.row.data.code);

      const dxContextMenuEvent = {
        ...e.event,
        type: 'dxcontextmenu',
      };
      e.event.stopPropagation();

      const actionCellElement = getGridActionCellElement(e);

      if (!actionCellElement) return;

      trigger(actionCellElement, dxContextMenuEvent);
    },

    viewAtc(e) {
      this.setSampleFetchedFromEtl(false);
      this.setLastRouteHome(false);
      this.setCurrentSampleId(e.row.data.id);
      this.setReportSampleId(e.row.data.sample_id);
      this.setCurrentFilter('foodDetails');
      this.setCurrentSample(e.row.data);
      this.setIsNoRp(e.row.data.value_report === 'CMA');
      this.setSelectedLimits([this.$t('positives')]);
      this.setReportSampleCode(e.row.data.code);
      let positiveParameters = [];
      if (e.row.data.value_report !== 'CMA') {
        if (e.row.data.retailers) {
          const chains = [...e.row.data.retailers];
          // positiveParameters =
          //   e.row.data.positive_parameters_retailers || [];

          const rawParameters =
            e.row.data.positive_parameters_retailers || [];

          const uniqueParameters = Array.from(
            new Map(
              rawParameters.map((p) => [p.Parametro, p])
            ).values()
          );

          positiveParameters = uniqueParameters;

          const res = [];

          chains.forEach((chainData) => {
            chainData.Reglas.forEach((chain) => {
              const reglaFormatted = chain.Regla.includes('.')
                ? chain.Regla.split('.').join('')
                : chain.Regla;

              if (
                res.length > 0 &&
                res.some(
                  (el) => el.Supermercado === chainData.Supermercado
                )
              ) {
                const indexRes = res.findIndex(
                  (el) => el.Supermercado === chainData.Supermercado
                );

                res[indexRes] = {
                  ...res[indexRes],
                  [`Regla ${chain.Regla}`]: chain.Regla,
                  [`Resultado ${this.$t(
                    `chainsList.${reglaFormatted}`
                  )}`]: chain.Resultado,
                  [`CodRegla ${chain.Regla}`]: chain.CodRegla,
                  Supermercado: chainData.Supermercado,
                  Confirmado: chainData.Confirmado,
                };
              } else {
                res.push({
                  [`Resultado ${this.$t(
                    `chainsList.${reglaFormatted}`
                  )}`]: chain.Resultado,
                  [`Regla ${chain.Regla}`]: chain.Regla,
                  [`CodRegla ${chain.Regla}`]: chain.CodRegla,
                  Supermercado: chainData.Supermercado,
                  Confirmado: chainData.Confirmado,
                  ...chain,
                });
              }
            });
          });

          this.setEuropeanChains([
            {
              cadenas: res,
              parametrosPositivos: positiveParameters,
            },
          ]);
        } else {
          this.setEuropeanChains([]);
        }
      }

      this.replaceCurrent({
        name: this.$t('sampleSummary'),
        disabled: true,
        page: 'FoodSampleDetailPage',
        active: true,
      });

      this.$f7router.navigate(
        { name: 'FoodSampleDetailPage' },
        {
          animate: true,
          transition: 'f7-fade',
        }
      );
      this.applyCurrentFilters();
    },

    addMenuItems(e) {
      if (e.column.name !== 'buttons') {
        return;
      }
      if (e.target === 'content') {
        if (!e.items) e.items = [];

        e.items.push({
          icon: 'pdffile',
          text: this.$t('generalReport'),
          onItemClick: async () => {
            try {
              this.setIsDownloadingReport(true);
              this.notificationMessage = this.$t('downloading');

              const { sample_id, study_id, code } = e.row.data;
              const downloaded = await this.downloadDualReport({ sample_id, study_id, code });

              if (downloaded) {
                this.notificationMessage = this.$t('downloadedFile');
              } else {
                this.notificationMessage = '';
                this.$notifyDX(
                  { message: this.$t('sampleHasNoReport'), width: 450 },
                  'info',
                  2000
                );
              }
            } catch (error) {
              this.$notifyDX(
                { message: this.$t('reportNotAvailable'), width: 450 },
                'error',
                NOTIFY_TIME_LONG
              );
            } finally {
              setTimeout(() => (this.notificationMessage = ''), 2000);
              this.setIsDownloadingReport(false);
            }
          },
        });

        e.items.push({
          icon: 'pdffile',
          text: this.$t('tracesReport'),
          onItemClick: async () => {
            try {
              const DOC_TYPE_TRACES = 2;
              this.setIsDownloadingReport(true);
              this.notificationMessage = this.$t('downloading');
              this.setReportSampleCode(e.row.data.code);

              const report = await this.getReport(DOC_TYPE_TRACES);

              if (report.name && report.pdfB64) {
                const link = document.createElement('a');
                link.href = `data:application/octet-stream;base64,${report.pdfB64}`;
                link.download = `${e.row?.data?.code}_${DOC_TYPE_TRACES}.pdf`;
                link.click();

                URL.revokeObjectURL(link.href);

                this.notificationMessage = this.$t('downloadedFile');
              } else {
                this.notificationMessage = '';
                this.$notifyDX(
                  {
                    message: this.$t('sampleHasNoReport'),
                    width: 450,
                  },
                  'info',
                  2000
                );
              }
            } catch (error) {
              this.$notifyDX(
                {
                  message: this.$t('reportNotAvailable'),
                  width: 450,
                },
                'error',
                NOTIFY_TIME_LONG
              );
            } finally {
              setTimeout(() => {
                this.notificationMessage = '';
              }, 2000);
              this.setIsDownloadingReport(false);
            }
          },
          disabled: !e.row.data?.traces_report,
        });
      }
    },

    async downloadSelected(type) {
      switch (type) {
        case 'pdf':
          await this.downloadPdf();
          break;
        case 'excel':
          this.onExporting(this.grid);
          break;
        case 'excelParams':
          this.downloadExcelWithParameters();
          break;
        default:
          break;
      }
    },

    async downloadPdf() {
      this.setDisableContext(true);

      try {
        this.notificationMessage = this.$t('downloading');
        const b64 = await this.getSamplesZip(
          this.getSamplesPageDownloadParams()
        );

        const link = document.createElement('a');
        link.href = `data:application/octet-stream;base64,${b64}`;
        link.download = `besafer-reports-PDF-${new Date().toLocaleString()}.zip`;
        link.click();

        URL.revokeObjectURL(link.href);

        this.notificationMessage = this.$t('downloadedFile');
      } catch (e) {
        this.notificationMessage = '';
        this.$notifyDX(
          {
            message: this.$t(
              this.$helpers.getFilteredErrorMessage(
                JSON.parse(e.message).error
              )
            ),
            width: 550,
          },
          'error',
          NOTIFY_TIME_LONG
        );
      } finally {
        setTimeout(() => {
          this.notificationMessage = '';
        }, 2000);
        this.setDisableContext(false);
      }
    },

    async downloadExcelWithParameters() {
      try {
        let message = this.$t('downloading');
        if (
          this.grid.component.totalCount() >
          ALLOWED_SAMPLES_PARAMETERS
        ) {
          message = this.$t('sampleParametersMustFilter');
        }
        this.notificationMessage = message;
        const samples = await this.getSamplesWithParameters(
          this.disableFilterMonitoring
        );
        this.formatExcel(samples);
      } catch (e) {
        this.notificationMessage = '';

        this.$notifyDX(
          {
            message: this.$t('downloadError'),
            width: 350,
          },
          'error',
          NOTIFY_TIME_LONG
        );
      } finally {
        setTimeout(() => {
          this.notificationMessage = '';
        }, 2000);
      }
    },

    onCellPrepared(e) {
      if (e.rowType === 'data') {
        if (!e.data.seen) {
          e.cellElement.classList.add('hasntBeenSeen');
        }
      }
    },

    onExporting(e) {
      this.notificationMessage = this.$t('samplesDownloading');
      e.component.beginUpdate();
      e.component.columnOption('ID', 'visible', true);
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet(
        `${`${this.$t(
          'environmentSamples.exportExcelSampleTable'
        )}-${this.$t('Food')}`}`
      );

      exportDataGrid({
        component: e.component,
        worksheet,
        customizeCell: ({ gridCell, excelCell }) => {
          if (
            gridCell.rowType === 'data' &&
            this.selectedEuropeanChains.includes(gridCell.column.name)
          ) {
            const color = this.getDotColorText(
              gridCell.column.caption,
              gridCell.data.value_report,
              gridCell.data.code
            );
            excelCell.value = color;

            switch (color) {
              case 'green':
                excelCell.font = { color: { argb: 'FF00B050' } };
                break;
              case 'red':
                excelCell.font = { color: { argb: 'FFFF0000' } };
                break;
              case 'grey':
                excelCell.font = { color: { argb: 'FF808080' } };
                break;
              default:
                excelCell.font = { color: { argb: 'FF000000' } };
                break;
            }
          }
        },
      })
        .then(() => {
          return workbook.xlsx.writeBuffer().then((buffer) => {
            saveAs(
              new Blob([buffer], {
                type: 'application/octet-stream',
              }),
              `besafer-samples-${this.$helpers.getDateFormatedDayMonthYear(
                new Date()
              )}.xlsx`
            );
          });
        })
        .then(() => {
          e.component.columnOption('ID', 'visible', false);
          e.component.endUpdate();
          setTimeout(() => (this.notificationMessage = ''), 2000);
        });

      e.cancel = true;
    },

    ...mapActions('filter', [
      'setCurrentFilter',
      'applyCurrentFilters',
      'setCurrentSampleId',
    ]),
    ...mapActions('foodSamples', [
      'setSamples',
      'setIsNoRp',
      'setEuropeanChains',
    ]),
    ...mapActions('samples', [
      'setCurrentSample',
      'setReportSampleCode',
      'setDisableContext',
      'getSamplesWithParameters',
      'setAllFilterParams',
      'getSamplesZip',
      'setIsDownloadingReport',
      'setSampleFetchedFromEtl',
      'setLastRouteHome',
    ]),
    ...mapActions('samplesRegisterModule', [
      'RegisterInvoice',
      'setInvoiceSamples',
    ]),
    ...mapActions('foodDetailsFilter', ['setSelectedLimits']),
    ...mapActions('breadcrumb', ['replaceCurrent']),
  },
};
</script>

<style>
.custom-grid .combined-status-cell {
  display: flex;
  align-items: center;
}
.custom-grid .combined-status-cell .status-dot {
  margin-right: 3px;
  flex-shrink: 0;
}
/** 
.custom-grid-food-samples .dx-toolbar-before {
  padding-right: 0px !important;
  display: grid;
  grid-template-columns: 123px 152px 141px 131px 158px;
  grid-template-rows: repeat(2, 1fr);
  row-gap: 24px;
  align-items: center;
  box-sizing: border-box;
}
*/
.custom-grid-food-samples .dx-toolbar-before {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(115px, max-content));
  grid-auto-rows: 48px;
  align-items: center;
  row-gap: 5px;
  width: 100%;
  box-sizing: border-box;
}

.custom-grid-food-samples .dx-toolbar-before :nth-child(1) {
  grid-row: 1;
  grid-column: 1 / span 3;
}
.custom-grid-food-samples .dx-toolbar-before :nth-child(2) {
  grid-row: 2;
  grid-column: 1;
}
.custom-grid-food-samples .dx-toolbar-before :nth-child(3) {
  grid-row: 2;
  grid-column: 2;
}
.custom-grid-food-samples .dx-toolbar-before :nth-child(4) {
  grid-row: 2;
  grid-column: 3;
}
.custom-grid-food-samples .dx-toolbar-before :nth-child(5) {
  grid-row: 2;
  grid-column: 4;
}
.custom-grid-food-samples .dx-toolbar-before :nth-child(6) {
  grid-row: 2;
  grid-column: 5;
}
.custom-grid-food-samples .dx-toolbar-before :nth-child(7) {
  grid-row: 2;
  grid-column: 6;
}
.custom-grid-food-samples .dx-toolbar-before :nth-child(8) {
  grid-row: 2;
  grid-column: 7;
}
.custom-grid-food-samples .dx-toolbar-before :nth-child(9) {
  grid-row: 2;
  grid-column: 8;
}
/* Aplica SOLO al botón “Último mes” */
.dx-toolbar .dx-button[name='Último mes'],
.dx-toolbar .dx-button[aria-label='Último mes'] {
  min-width: 160px !important; /* Aumenta el ancho */
}

/* También evita que el texto se corte */
/* .dx-toolbar .dx-button[name="Último mes"] .dx-button-text,
.dx-toolbar .dx-button[aria-label="Último mes"] .dx-button-text {
  white-space: nowrap !important;
} */

.dx-toolbar .dx-button[data-item='Último mes'] {
  min-width: 165px !important;
}

.dx-toolbar .dx-button[data-item='Último mes'] .dx-button-text {
  white-space: nowrap !important;
}
.botones-registrar.dx-button .dx-button-content {
  /* border: none !important; */
  padding-top: 7px !important;
  padding-bottom: 7px !important;
}
.btn-descargar .dx-dropdownbutton-action .dx-button-content::after {
  content: '';
  display: inline-block;
  width: 24px;
  height: 24px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'%3E%3Cpath d='M12 14.677C11.8795 14.677 11.7673 14.6577 11.6635 14.6192C11.5597 14.5807 11.4609 14.5147 11.3673 14.4212L6.873 9.927C6.73467 9.7885 6.66384 9.61441 6.6605 9.40475C6.65734 9.19525 6.72817 9.018 6.873 8.873C7.018 8.72816 7.19367 8.65575 7.4 8.65575C7.60634 8.65575 7.782 8.72816 7.927 8.873L12 12.9462L16.073 8.873C16.2115 8.73466 16.3856 8.66383 16.5953 8.6605C16.8048 8.65733 16.982 8.72816 17.127 8.873C17.2718 9.018 17.3443 9.19366 17.3443 9.4C17.3443 9.60633 17.2718 9.782 17.127 9.927L12.6328 14.4212C12.5391 14.5147 12.4403 14.5807 12.3365 14.6192C12.2327 14.6577 12.1205 14.677 12 14.677Z' fill='%2300346A'/%3E%3C/svg%3E");
  background-size: contain;
  background-repeat: no-repeat;
  margin-left: 6px;
}
/* Ocultar la flecha nativa de DevExtreme SOLO en el botón Descargar */
.btn-descargar .dx-icon-spindown {
  display: none !important;
}
/* Aplica SOLO al botón “Último mes” */
.custom-grid-food-samples
  .custom-toolbar
  .dx-toolbar
  .dx-button[name='En proceso'],
.custom-grid-food-samples
  .custom-toolbar
  .dx-toolbar
  .dx-button[aria-label='En proceso'] {
  min-width: 160px !important; /* Aumenta el ancho */
}
.registrar-arriba {
  grid-row: 1 !important;
  grid-column: 5 !important;
}

/* Descargar DEBAJO (fila 2, columna 5) */
.descargar-abajo {
  grid-row: 2 !important;
  grid-column: 5 !important;
}
/* Hacer dos filas en la parte derecha */
.custom-grid-food-samples .custom-toolbar .dx-toolbar-after {
  display: grid !important;
  grid-template-columns: 1fr;
  grid-template-rows: 48px 48px; /* altura exacta de las líneas */
  row-gap: 5px;
  justify-items: end;
}

/* Registrar Muestra en fila 1 */
.custom-grid-food-samples .dx-toolbar-after .registrar-arriba {
  grid-row: 1 !important;
}

/* Descargar en fila 2 */
.custom-grid-food-samples .dx-toolbar-after .descargar-abajo {
  grid-row: 2 !important;
}
.custom-grid-food-samples
  .custom-toolbar
  .dx-toolbar-after
  .btn-estado-custom
  .dx-button-content {
  padding: 0 16px !important;
  padding-right: 8px !important;
}
/* OCULTAR errores internos del DataGrid */
.dx-datagrid .dx-error-row {
  display: none !important;
}
@media (max-width: 1100px) {
  .custom-grid-food-samples .custom-toolbar .dx-toolbar-items-container {
    position: relative !important;
    height: auto !important;
    min-height: 0 !important;
    overflow: visible !important;
  }

  .custom-grid-food-samples .custom-toolbar .dx-toolbar-before {
    display: grid !important;
    grid-template-columns: repeat(6, minmax(0, 1fr)) !important;
    grid-template-rows: 48px 48px !important;
    gap: 10px;
    position: relative !important;
    width: 100% !important;
    min-width: 0 !important;
    padding: 0 8px 0 0 !important;
    box-sizing: border-box;
  }

  .custom-grid-food-samples .custom-toolbar .dx-toolbar-before > * {
    grid-row: auto !important;
    grid-column: auto !important;
  }

  .custom-grid-food-samples .custom-toolbar .dx-toolbar-before > :nth-child(1) { grid-row: 1 !important; grid-column: 1 / span 2 !important; }
  .custom-grid-food-samples .custom-toolbar .dx-toolbar-before > :nth-child(2) { grid-row: 1 !important; grid-column: 3 !important; position: relative !important; left: -30px !important; top: -5px !important; }
  .custom-grid-food-samples .custom-toolbar .dx-toolbar-before > :nth-child(3) { grid-row: 2 !important; grid-column: 1 !important; }
  .custom-grid-food-samples .custom-toolbar .dx-toolbar-before > :nth-child(4) { grid-row: 2 !important; grid-column: 2 !important; }
  .custom-grid-food-samples .custom-toolbar .dx-toolbar-before > :nth-child(5) { grid-row: 2 !important; grid-column: 3 !important; }
  .custom-grid-food-samples .custom-toolbar .dx-toolbar-before > :nth-child(6) { grid-row: 2 !important; grid-column: 4 !important; }
  .custom-grid-food-samples .custom-toolbar .dx-toolbar-before > :nth-child(7) { grid-row: 2 !important; grid-column: 5 !important; }
  .custom-grid-food-samples .custom-toolbar .dx-toolbar-before > :nth-child(8) { grid-row: 2 !important; grid-column: 6 !important; }

  .custom-grid-food-samples .custom-toolbar .dx-toolbar-after {
    display: flex !important;
    flex-wrap: nowrap !important;
    position: absolute !important;
    top: 0 !important;
    left: auto !important;
    right: 0 !important;
    gap: 8px;
    align-items: center !important;
    width: auto !important;
    min-width: max-content !important;
    padding: 0 !important;
    z-index: 2;
  }
}
@media (max-width: 950px) {
  .custom-grid-food-samples .custom-toolbar .dx-toolbar-before {
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    grid-template-rows: 48px 48px 48px 48px !important;
  }

  .custom-grid-food-samples .custom-toolbar .dx-toolbar-before > :nth-child(1) {
    grid-row: 1 !important;
    grid-column: 1 / span 2 !important;
  }

  .custom-grid-food-samples .custom-toolbar .dx-toolbar-before > :nth-child(2) {
    grid-row: 2 !important;
    grid-column: 1 !important;
    left: 0 !important;
    top: 0 !important;
  }

  .custom-grid-food-samples .custom-toolbar .dx-toolbar-before > :nth-child(3),
  .custom-grid-food-samples .custom-toolbar .dx-toolbar-before > :nth-child(4),
  .custom-grid-food-samples .custom-toolbar .dx-toolbar-before > :nth-child(5) {
    grid-row: 3 !important;
  }

  .custom-grid-food-samples .custom-toolbar .dx-toolbar-before > :nth-child(3) { grid-column: 1 !important; }
  .custom-grid-food-samples .custom-toolbar .dx-toolbar-before > :nth-child(4) { grid-column: 2 !important; }
  .custom-grid-food-samples .custom-toolbar .dx-toolbar-before > :nth-child(5) { grid-column: 3 !important; }

  .custom-grid-food-samples .custom-toolbar .dx-toolbar-before > :nth-child(6),
  .custom-grid-food-samples .custom-toolbar .dx-toolbar-before > :nth-child(7),
  .custom-grid-food-samples .custom-toolbar .dx-toolbar-before > :nth-child(8) {
    grid-row: 4 !important;
  }

  .custom-grid-food-samples .custom-toolbar .dx-toolbar-before > :nth-child(6) { grid-column: 1 !important; }
  .custom-grid-food-samples .custom-toolbar .dx-toolbar-before > :nth-child(7) { grid-column: 2 !important; }
  .custom-grid-food-samples .custom-toolbar .dx-toolbar-before > :nth-child(8) { grid-column: 3 !important; }
}
</style>
